import {ActionTree, GetterTree, Module, MutationTree} from "vuex";
import {elevationGainFromCoordinates} from "../../../../utils/elevation/elevationGain";
import {RootState} from "../store";
import graphClient from "../../../../utils/graphql/graph_client";
import {GetTrailMap} from "../../../../utils/graphql/trail-graph";
import * as turf from "@turf/turf";
import {bbox as turfBbox, distance as turfDistance} from "@turf/turf";
import {GridRefUtils} from "../../../../utils/helpers/GridRefPreference";
import {MeasurementPrefUtility} from "../../../../utils/helpers/MeasurementPref";
import {HiikerColor} from "../../../../utils/helpers/HiikerColor";
import measurementMarker from "../../../../images/measurement_marker.png";
import {HikeTimeUtility} from "../../../../utils/helpers/HikeTime";
import {isClickOnWaypointHelper} from "../../../../utils/helpers/IsClickOnWaypointHelper";
import mapboxgl from "mapbox-gl";
import {appCheck} from "../../../../utils/firebase/appCheck";
import {getToken} from "firebase/app-check";
import {IndexedDBCache} from "../../../../utils/crosshair_altitude_service/indexed_db_cache";
import {getElevation} from "../../../../utils/crosshair_altitude_service/pixel_methods";
import {throttle} from 'lodash-es';
import {HiikerMapStyle} from "../../../../utils/models/MapStyles";
import {RevenueCatWrapper} from "../../../../utils/RevenueCatWrapper";
import {MapLayerCategory} from "../../../../utils/layer-registry";


export class TrailOverviewMapState {
  trailId: number | null = null
  center: mapboxgl.LngLatLike = [0,0]
  zoom: number = 13
  trail: any = null
  loading: boolean = false
  mapInitialised: boolean = false
  miniCoordsBar: string = ''
  mapBoundsSet: boolean = false
  measurementMode: boolean = false
  regionSlug: string | null = null
  trailSlug: string | null = null
  countrySlug: string | null = null
  measuredGeoJson: any = null
  trailSetOnMap: boolean = false
  stage: string | null = null
  isForStage: boolean = false
  scaleAdded: boolean = false
}

let markers: any = []
let startEndMarkers: mapboxgl.Marker[] = []

const actions: ActionTree<TrailOverviewMapState, RootState> = {
  async initialiseStore(store) {
    store.commit('setStage')
    store.commit('setSlugData')
    store.commit('setCenter', [parseFloat(window.trailCenterX), parseFloat(window.trailCenterY)])
    store.commit('setTrailId', window.trailId)
    // @ts-ignore

    await store.dispatch('getTrail')
    const title = document.getElementById("main-title")
    const body = document.getElementById("main-body")
    if (title && body) {
      title.style.display = "none"
      body.style.display = "none"
    }
  },

  mapInitialised(store) {
    store.commit('setMapInitialised', true)
    const gridRef = GridRefUtils.preference()
    let measurementPreference = MeasurementPrefUtility.preference()
    // @ts-ignore
    if (!store.state.scaleAdded) {
      store.state.scaleAdded = true
      window.map.addControl(new mapboxgl.ScaleControl({ unit: measurementPreference.mapbox(), maxWidth: 150 }), 'bottom-left');
    }
    
    window.map.on('click', (e) => {
      store.dispatch('mapClicked', e);
    });
    const cache = new IndexedDBCache();

    const handleMouseMove = async (e) => {
      const coords = gridRef.convert(e.lngLat);
      const elevation = await getElevation(e.lngLat, e.target.getZoom(), cache);
      store.commit('setMiniCoordsBar', `${coords.toString()} ${elevation}`);
    };

    const throttledMouseMove = throttle(handleMouseMove, 20); // Adjust the throttle interval as needed

    window.map.on('mousemove', throttledMouseMove);
  },
  async getTrail(store, skipMapSetting = false) {
    try {
      if (store.state.trail) {
        if (!skipMapSetting) {
          await store.dispatch('setTrailOnMap')
        }
        return
      }
      let headers = {}
      try {
        const appCheckTokenResponse = await getToken(appCheck, false)
        headers = { 'X-Firebase-AppCheck': appCheckTokenResponse.token }
      } catch (e) {
        console.error(e);
      }
      let trailRequest = await graphClient.request(GetTrailMap,
        {id: store.state.trailId}, headers,
      )
      store.commit('setTrail', trailRequest.trail)
      if (!skipMapSetting) {
        await store.dispatch('setTrailOnMap')
      }
    } catch (e) {
      // @ts-ignore
      alert(e.message)
    }
  },
  async setTrailOnMap(store) {
    if (!store.state.mapInitialised) {
      return;
    }
    const trail = store.state.trail;
    if (trail) {
      store.dispatch('removeTrailFromMap');
      let trailId = 'trail';

      // Get the trail GeoJSON feature
      const trailGeoJsonFeature = store.getters.getTrailLine;

      // Extract coordinates from the trail GeoJSON
      const coordinates = store.getters.getTrailGeoJsonFeature.geometry.coordinates;

      // Identify start and end coordinates
      const startCoordinates = coordinates[0];
      const endCoordinates = coordinates[coordinates.length - 1];

      let trailBorderLayer = {
        id: trailId + "-border",
        type: "line",
        source: trailId,
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#574243",  // Set this to the border color you want
          "line-width": 6.5,          // Border width (slightly larger than the inner line)
        }
      };

      let trailLayer = {
        id: trailId,
        type: "line",
        source: trailId,
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#DB2B35",
          "line-width": 3.5,
        }
      };
      window.layerRegistry.addSource(trailId, {
        type: "geojson",
        data: trailGeoJsonFeature,
      })
      window.layerRegistry.addLayer(trailLayer, MapLayerCategory.Annotations, {z: 10});
      window.layerRegistry.addLayer(trailBorderLayer, MapLayerCategory.Annotations, {z: 9});

      await store.dispatch('addPolylineArrowLayerToMap');
      if (!store.state.mapBoundsSet) {
        await store.dispatch('setMapBoundsFromPolygonGeoJson');
        await store.dispatch('checkForPOIParam');
      }

      // Add start and end markers using sprite sheet icons
      store.dispatch('addStartEndMarkers', { startCoordinates, endCoordinates });
    }
  },

  addStartEndMarkers(store, { startCoordinates, endCoordinates }) {
    const map = window.map;
    if (!map) return;

    // Remove existing start/end markers
    store.dispatch('removeStartEndMarkers');

    // Create GeoJSON sources for start and end points
    const startPointSource = {
      type: 'geojson' as const,
      data: {
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: startCoordinates
        },
        properties: {}
      }
    };

    const endPointSource = {
      type: 'geojson' as const,
      data: {
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: endCoordinates
        },
        properties: {}
      }
    };

    // Add sources
    window.layerRegistry.addSource('trail-start-point', startPointSource);
    window.layerRegistry.addSource('trail-end-point', endPointSource);

    // Add start marker layer
    const startMarkerLayer = {
      id: 'trail-start-marker',
      type: 'symbol' as const,
      source: 'trail-start-point',
      layout: {
        'icon-image': 'hike-control-point-start',
        'icon-size': 0.25,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': 'center' as const
      }
    };

    // Add end marker layer
    const endMarkerLayer = {
      id: 'trail-end-marker',
      type: 'symbol' as const,
      source: 'trail-end-point',
      layout: {
        'icon-image': 'hike-control-point-end',
        'icon-size': 0.25,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': 'center' as const
      }
    };

    // Add layers with high z-index to ensure they appear on top
    window.layerRegistry.addLayer(startMarkerLayer, MapLayerCategory.Annotations, {z: 20});
    window.layerRegistry.addLayer(endMarkerLayer, MapLayerCategory.Annotations, {z: 20});
  },

  removeStartEndMarkers(store) {
    const map = window.map;
    if (!map) return;

    // Remove layers
    if (map.getLayer('trail-start-marker')) {
      window.layerRegistry.removeLayer('trail-start-marker');
    }
    if (map.getLayer('trail-end-marker')) {
      window.layerRegistry.removeLayer('trail-end-marker');
    }

    // Remove sources
    if (map.getSource('trail-start-point')) {
      window.layerRegistry.removeSource('trail-start-point');
    }
    if (map.getSource('trail-end-point')) {
      window.layerRegistry.removeSource('trail-end-point');
    }
  },

  checkForPOIParam(store) {
    const params = new URLSearchParams(window.location.search);

    const id = params.get('id');
    const type = params.get('type');

    if (id && type) {
      store.dispatch('poi/addPOIToMapByID', {id: id, type: type}, { root: true })
    }
  },
  removeTrailFromMap(store) {
    if (!store.state.mapInitialised) {
      return
    }
    const map = window.map
    if (map?.getLayer('trail')) {
      map.removeLayer('trail')
    }
    if (map?.getLayer('trail-arrow')) {
      map.removeLayer('trail-arrow')
    }
    // Remove start/end markers
    store.dispatch('removeStartEndMarkers');
  },
  setMapBoundsFromPolygonGeoJson(store) {
    const map = window.map
    const newBbox = turfBbox(store.getters.getTrailGeoJsonFeature);
    // @ts-ignore
    const bounds = mapboxgl.LngLatBounds.convert(newBbox);
    map?.fitBounds(bounds, { padding: 100 });
    store.commit('setMapBoundsSet', true)
  },
  addPolylineArrowLayerToMap(
    store,
  ) {
    const arrowLayer = 'trail-arrow'
    const targetLayer = 'trail'

    if (window.map.getLayer(arrowLayer)) {
      window.map.removeLayer(arrowLayer);
    }
    const arrowLayerLayer = {
      id: arrowLayer,
      type: "symbol",
      source: targetLayer,
      layout: {
        "symbol-placement": "line",
        "icon-allow-overlap": true,
        'icon-ignore-placement': true,
        "icon-image": "hike-arrow-icon",
        "icon-size": 0.2,
        visibility: "visible",
      },
    }
    window.layerRegistry.addLayer(arrowLayerLayer, MapLayerCategory.Annotations, {z: 12});
  },
  mapClicked(store, event: mapboxgl.MapMouseEvent) {
    if (store.rootState.waypoints.waypointAddingModeEnabled) {
      return;
    }
    if (isClickOnWaypointHelper(window.map.queryRenderedFeatures(event.point))) {
      return;
    }
    if (store.state.measurementMode) {
      store.dispatch('addMeasurementMarkerToMap', event)
      console.log('map clicked with measurement mode on')
    } else {
      console.log('map clicked')
    }
  },
  async addMeasurementMarkerToMap(store, event: mapboxgl.MapMouseEvent) {
    console.log('adding marker')
    // @ts-ignore
    if (markers.length > 1) {
      return
    }
    const markerOption: mapboxgl.MarkerOptions = {
      element: markers.length == 0 ? store.getters.marker1Element : store.getters.marker2Element,
      draggable: true,
      anchor: 'center',
      offset: [0, -12]
    }
    // @ts-ignore
    let point = turf.point(event.lngLat.toArray());
    let closestPoint = turf.nearestPointOnLine(store.getters.getLineString, point);
    const marker = new mapboxgl.Marker(markerOption).setLngLat(
      // @ts-ignore
      closestPoint.geometry.coordinates.slice(0, 2)
    ).addTo(
      window.map
    ).on('dragend', async (e) => {
      await store.dispatch('measurementMarkerDragged', e)
    })
    markers.push(marker)
    await store.dispatch('updateMeasurementLine')
  },
  async measurementMarkerDragged(store, event) {
    console.log("Marker dragged")
    let markerLocation = event.target.getLngLat();

    // Convert the marker location to a GeoJSON Point object
    let point = turf.point([markerLocation.lng, markerLocation.lat]);

    // Find the closest point on the linestring to the marker location
    let closestPoint = turf.nearestPointOnLine(store.getters.getLineString, point);
    // @ts-ignore
    event.target.setLngLat(closestPoint.geometry.coordinates.slice(0, 2));
    await store.dispatch('updateMeasurementLine')
  },
  async updateMeasurementLine(store) {
    // @ts-ignore
    if (markers.length > 1) {
      // @ts-ignore
      console.log('updating measurement line')
      const location1 = turf.point(markers[0].getLngLat().toArray())
      const location2 = turf.point(markers[1].getLngLat().toArray())
      const newLineString = turf.lineSlice(location1, location2, store.getters.getLineString);
      store.commit('setMeasuredGeoJson', newLineString)
      const lineLayerId = 'measurement'
      const map = window.map
      if (!map) {
        throw new Error('Map not set')
      }
      if (map.getLayer(lineLayerId)) {
        map.removeLayer(lineLayerId)
      }
      if (map.getSource(lineLayerId)) {
        map.removeSource(lineLayerId)
      }
      let source: mapboxgl.GeoJSONSource = {
        type: 'geojson',
        // @ts-ignore
        data: newLineString
      }
      map.addSource(lineLayerId, source)
      map.addLayer(store.getters.getMeasurementLayer)
    }
  },
  setLoading({ commit }, payload) {
    commit('SET_LOADING', payload);
  },
}

const mutations: MutationTree<TrailOverviewMapState> = {
  SET_LOADING(state, payload) {
    state.loading = payload;
  },
  setTrailId(state, trailId: number) {
    state.trailId = trailId
  },
  setTrail(state, trail: any) {
    state.trail = trail
  },
  setMapInitialised(state, mapInitialised: boolean) {
    state.mapInitialised = mapInitialised
  },
  setCenter(state, center: mapboxgl.LngLatLike) {
    state.center = center
  },
  setMiniCoordsBar(state, miniCoordsBar: string) {
    state.miniCoordsBar = miniCoordsBar
  },
  setMapBoundsSet(state, mapBoundsSet: boolean) {
    state.mapBoundsSet = mapBoundsSet
  },
  setSlugData(state) {
    state.trailSlug = window.slug;
    state.regionSlug = window.region_slug;
    state.countrySlug = window.country_slug;
    state.trailId = window.id
  },
  setStage(state) {
    state.isForStage = window.isForStage
  },
  startMeasurementMode(state) {
    state.measurementMode = true
  },
  stopMeasurementMode(state) {
    state.measurementMode = false
    // @ts-ignore
    markers.forEach((marker) => {
      marker.remove()
    })
    markers.forEach((marker) => {
      marker.remove()
    })
    markers = []
    if (state.measuredGeoJson) {
      window.map.removeLayer('measurement')
      window.map.removeSource('measurement')
    }
    state.measuredGeoJson = null
  },
  setMeasuredGeoJson(state, geoJson: any) {
    state.measuredGeoJson = geoJson
  }
}

// @ts-ignore
const getters: GetterTree<TrailOverviewMapState, RootState> = {
  async initialMapSettings(state): Promise<mapboxgl.MapboxOptions> {
    // @ts-ignore
    const isPro = await RevenueCatWrapper.isProOrProPlus()
    const style = HiikerMapStyle.getCurrentStyle(isPro).styleUrl
    return {
      container: 'map', // container ID
      center: state.center, // starting position [lng, lat]
      zoom: state.zoom, // starting zoom
      maxPitch: 85, // limit the pitch to 85 degreesl
      style: style, // style URL
    }
  },
  // @ts-ignore
  getTrailLayer(): mapboxgl.LineStyleLayer {
    return {
      id: 'trail',
      type: "line",
      source: 'trail',
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      paint: {
        "line-color": HiikerColor.red,
        "line-width": 5,
      }
    }
  },
  getMeasurementLayer(): mapboxgl.LineLayer {
    let lineLayer: mapboxgl.LineLayer = {
      id: 'measurement',
      type: "line",
      source: 'measurement',
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      paint: {
        "line-color": HiikerColor.green,
        "line-width": 5,
      }
    }
    return lineLayer
  },
  getTrailGeoJsonFeature(state) {
    if (state.isForStage) {
      return window.pathLineStringGeoJson.features[0]
    } else {
      return JSON.parse(state.trail.lineString.geoData).features[0]
    }
  },
  getTrailLine(state) {
    if (state.isForStage) {
      return window.pathLineStringGeoJson.features[0]
    } else {
      return JSON.parse(state.trail.multilinestring).features[0]
    }
  },
  getLineString(state, getters) {
    return turf.lineString(getters.getTrailGeoJsonFeature.geometry.coordinates)
  },
  createChartData(state, getters) {
    if (state.trail == null) {
      return null
    }
    const coordinates = getters.getTrailGeoJsonFeature.geometry.coordinates
    const chartdata: any = {
      labels: [],
      xLabels: [],
      lat: [],
      lon: [],
      datasets: [
        {
          borderColor: HiikerColor.red,
          borderWidth: 3,
          fill: false,
          data: [],
        },
      ],
    };
    let divider = 0;
    if (coordinates.length > 1000) {
      divider = Math.round(coordinates.length / 1000);
    }
    let count = 0;
    let distance = 0;
    const distances = coordinates.map((coordinate: any, index: number) => {
      if (index === 0) {
        return 0;
      } else {
        distance += Math.round(turfDistance(coordinates[index - 1], coordinate, { units: 'meters' }))
        return distance;
      }
    })
    const measurementUtils = MeasurementPrefUtility.preference()
    for (let i = 0; i < coordinates.length; i++) {
      if (count === divider) {
        count = 0;
        const coord = coordinates[i];
        chartdata.xLabels.push(measurementUtils.bigUnitFormatter(distances[i] / 1000, false));
        chartdata.labels.push(measurementUtils.smallUnitFormatter(coord[2]));
        chartdata.lat.push(coord[1]);
        chartdata.lon.push(coord[0]);
        // {x, y} keyed on real cumulative distance so the linear x-axis spaces
        // points by distance, not array index (avoids the "stepping" artifact
        // on tracks with long, sparsely-sampled straight segments).
        chartdata.datasets[0].data.push({ x: measurementUtils.bigUnitValue(distances[i] / 1000), y: measurementUtils.smallUnitValue(coord[2]) });
      } else {
        count++;
      }
    }
    return chartdata;
  },
  trailUrl(state) {
    return `/trails/${state.countrySlug}/${state.regionSlug}/${state.trailSlug}`;
  },
  measureGeoJsonChartData(state, getters) {
    if (state.measuredGeoJson == null) {
      return null
    }
    const coordinates = state.measuredGeoJson.geometry.coordinates
    const chartdata: any = {
      labels: [],
      xLabels: [],
      lat: [],
      lon: [],
      datasets: [
        {
          borderColor: HiikerColor.green,
          borderWidth: 3,
          fill: false,
          data: [],
        },
      ],
    };
    let divider = 0;
    if (coordinates.length > 1000) {
      divider = Math.round(coordinates.length / 1000);
    }
    let count = 0;
    let distance = 0;
    const distances = coordinates.map((coordinate: any, index: number) => {
      if (index === 0) {
        return 0;
      } else {
        distance += Math.round(turfDistance(coordinates[index - 1], coordinate, { units: 'meters' }))
        return distance;
      }
    })
    const measurementUtils = MeasurementPrefUtility.preference()
    for (let i = 0; i < coordinates.length; i++) {
      if (count === divider) {
        count = 0;
        const coord = coordinates[i];
        chartdata.xLabels.push(measurementUtils.bigUnitFormatter(distances[i] / 1000, false));
        chartdata.labels.push(measurementUtils.smallUnitFormatter(coord[2]));
        chartdata.lat.push(coord[1]);
        chartdata.lon.push(coord[0]);
        // {x, y} keyed on real cumulative distance so the linear x-axis spaces
        // points by distance, not array index (avoids the "stepping" artifact
        // on tracks with long, sparsely-sampled straight segments).
        chartdata.datasets[0].data.push({ x: measurementUtils.bigUnitValue(distances[i] / 1000), y: measurementUtils.smallUnitValue(coord[2]) });
      } else {
        count++;
      }
    }
    return chartdata;
  },
  measuredGeoJsonDistance(state) {
    const distance = turf.length(state.measuredGeoJson, { units: 'meters' });
    const measurementPreference = MeasurementPrefUtility.preference();
    return measurementPreference.smallUnitFormatter(distance, true);
  },
  measuredGeoJsonElevationGain(state) {
    const measurementPreference = MeasurementPrefUtility.preference();
    // Canonical 6m moving-baseline gain. The loop this replaces seeded prevElevation at 0,
    // so the first sample's whole altitude counted as climb -- a path starting at 500m
    // opened with 500m already banked -- and it summed raw deltas on top of that.
    const elevationGain = elevationGainFromCoordinates(state.measuredGeoJson.geometry.coordinates);
    return measurementPreference.smallUnitFormatter(elevationGain);
  },
  measuredGeoJsonHikeTime(state, getters) {
    const distance = turf.length(state.measuredGeoJson, { units: 'kilometers' });
    // Canonical 6m moving-baseline gain. The loop this replaces seeded prevElevation at 0,
    // so the first sample's whole altitude counted as climb -- a path starting at 500m
    // opened with 500m already banked -- and it summed raw deltas on top of that.
    const elevationGain = elevationGainFromCoordinates(state.measuredGeoJson.geometry.coordinates);
    let timeToReturn = 0.0
    const hikeTime = HikeTimeUtility.preference()
    timeToReturn += (distance / hikeTime.kmDistance())
    timeToReturn += (elevationGain / hikeTime.meterElevationGain())
    let seconds = (timeToReturn * 60) * 60
    let hours = Math.floor(seconds / 3600);
    let minutes = Math.floor((seconds % 3600) / 60);
    let timeString = "";

    if (hours > 0) {
      timeString += hours + (hours > 1 ? " hours " : " hour ");
    }
    if (minutes > 0) {
      timeString += minutes + (minutes > 1 ? " mins" : " min");
    }
    return "~ " + timeString;
  },
  marker1Element() {
    const el = document.createElement('div');
    el.id = 'marker' + Math.random();
    el.style.position = 'absolute';
    el.style.width = `${26}px`;
    el.style.height = `${83}px`;
    el.style.backgroundSize = 'contain';
    el.style.backgroundRepeat = 'no-repeat';
    el.style.cursor = 'pointer';
    el.style.backgroundPosition = 'center center';
    el.style.backgroundImage = `url(${measurementMarker})`;
    return el;
  },
  marker2Element() {
    const el = document.createElement('div');
    el.id = 'marker' + Math.random();
    el.style.position = 'absolute';
    el.style.width = `${26}px`;
    el.style.height = `${83}px`;
    el.style.backgroundSize = 'contain';
    el.style.backgroundRepeat = 'no-repeat';
    el.style.cursor = 'pointer';
    el.style.backgroundPosition = 'center center';
    el.style.backgroundImage = `url(${measurementMarker})`;
    return el;
  }
}

const state: TrailOverviewMapState = new TrailOverviewMapState();

const trailOverviewMapModule: Module<TrailOverviewMapState, RootState> = {
  namespaced:true,
  state:state,
  actions:actions,
  mutations:mutations,
  getters:getters,
}

export default trailOverviewMapModule;
